
import React, {FunctionComponent, ReactNode, useRef, useState} from "react";
import {useHistory} from "react-router";
import {useTranslation} from "react-i18next";
import produce from "immer";

import './Header.scss';
import {Button, DeviceCounts, ISplitButtonProps, SplitButton, StateBar, StateColor, StateIcon, TargetCounts, UpdateHelpModal} from "components";
import {AsyncStatus, StageState, UpdateState, UserRole} from "enums";
import {UsersService} from "services";
import {ApproveModal, ApproveModalMode} from "./ApproveModal";
import {CancelUpdateModal} from "pages/Update/Components/CancelUpdateModal";
import {DeleteModal} from "pages/Update/Components/DeleteModal";
import {RevertDraftModal} from "pages/Update/Components/RevertDraftModal";
import {UpdatePageModel} from "../UpdatePageModel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown } from "react-bootstrap";
import { getDefaultUpdateParams_update, getUpdateResultsURL } from "pages/Reports/ReportsModel";
import { Link } from "react-router-dom";
import { SaveTemplateModal } from "./SaveTemplateModal";

const stateTexts = {
    [UpdateState.Draft]: `This update will need to be approved before it can be deployed.`,
    [UpdateState.AwaitApproval]: `A user needs to approve this update before it can be deployed. `
        + `To make changes, revert it to a draft.`,
    [UpdateState.Scheduled]: `This update is scheduled to be deployed.`,
    [UpdateState.Running]: `Sending update to devices...`,
    [UpdateState.Canceled]: `This update has been canceled.`,
    [UpdateState.Error]: `An internal server error has occured.`,
    [UpdateState.Done]: 'Update successfully completed.',
}

interface IHeaderProps {
    model: UpdatePageModel,
}

interface IHeaderState {
    approveModalMode?: ApproveModalMode,
    showApproveModal?: boolean,
    showCancelModal?: boolean,
    showDeleteModal?: boolean,
    showRevertDraftModal?: boolean,
    showUpdateHelpModal?: boolean,
    showSaveTemplateModal?: boolean,
}

export const Header: FunctionComponent<IHeaderProps> = function Header(props) {
    const { t } = useTranslation();
    const history = useHistory();
    const { model } = props;
    const { update } = model;
    const [state, setState] = useState<IHeaderState>({})
    const {
        approveModalMode,
        showApproveModal,
        showCancelModal,
        showDeleteModal,
        showRevertDraftModal,
        showUpdateHelpModal,
        showSaveTemplateModal,
    } = state;

    // Modal key refs so that we can reset the modals every time we show them.
    const approveModalKeyRef = useRef(1);
    const cancelUpdateModalKeyRef = useRef(1);
    const deleteModalKeyRef = useRef(1);
    const revertDraftModalKeyRef = useRef(1);
    const saveTemplateModalKeyRef = useRef(1);

    // ---------------------- Modals ----------------------

    function onSubmitForApproval() {
        approveModalKeyRef.current++;
        setState(produce(draft => {
            draft.approveModalMode = ApproveModalMode.SubmitForApproval;
            draft.showApproveModal = true;
        }));
    }

    function onStartApprove() {
        approveModalKeyRef.current++;
        setState(produce(draft => {
            draft.approveModalMode = ApproveModalMode.Approve;
            draft.showApproveModal = true;
        }));
    }

    function closeApproveModal() {
        setState(produce(draft => {
            draft.showApproveModal = false;
        }));
    }

    function openDeleteModal() {
        deleteModalKeyRef.current++;
        setState(produce(draft => {
            draft.showDeleteModal = true;
        }));
    }

    function closeDeleteModal() {
        setState(produce(draft => {
            draft.showDeleteModal = false;
        }))
    }

    function onDeleteSuccess() {
        closeDeleteModal();
        // Redirect to updates list page
        history.push("/");
    }

    function openCancelUpdateModal() {
        cancelUpdateModalKeyRef.current++;
        setState(produce(draft => {
            draft.showCancelModal = true;
        }));
    }

    function openUpdateHelpModal() {
        setState(produce(draft => {
            draft.showUpdateHelpModal = true;
        }));
    }

    function closeCancelUpdateModal() {
        setState(produce(draft => {
            draft.showCancelModal = false;
        }));
    }

    function openRevertDraftModal() {
        revertDraftModalKeyRef.current++;
        setState(produce(draft => {
            draft.showRevertDraftModal = true;
        }));
    }

    function closeRevertDraftModal() {
        setState(produce(draft => {
            draft.showRevertDraftModal = false;
        }));
    }

    function closeUpdateHelpModal() {
        setState(produce(draft => {
            draft.showUpdateHelpModal = false;
        }));
    }

    function openSaveTemplateModal() {
        saveTemplateModalKeyRef.current++;
        setState(produce(draft => {
            draft.showSaveTemplateModal = true;
        }));
    }

    function closeSaveTemplateModal() {
        setState(produce(draft => {
            draft.showSaveTemplateModal = false;
        }));
    }

    // ---------------------- Template ----------------------

    const isInAPICall = (model.apiCallStatus === AsyncStatus.Loading);
    const canModify = (UsersService.oktaUserInfo.role >= UserRole.LimitedUser);
    const resultsURLParams = getDefaultUpdateParams_update(update.id, update.state)
    const resultsURL = getUpdateResultsURL(resultsURLParams);

    let actionButtonProps: ISplitButtonProps;
    if (update.state === UpdateState.Draft) {
        actionButtonProps = {
            title: 'Start Submit',
            onClick: onSubmitForApproval,
            disabled: isInAPICall || !canModify,
        }
    } else if (update.state === UpdateState.AwaitApproval) {
        actionButtonProps = {
            title: 'Start Approval',
            onClick: onStartApprove,
            disabled: isInAPICall || !canModify || !model.isApprover,
        }
    } else {
        actionButtonProps = {
            title: 'View Results',
            as: 'Link',
            href: resultsURL,
        }
    }

    function isWaitingOnStageConfirm(): boolean {
        if (update.state !== UpdateState.Running) return false;

        // Loop backwards to find the latest done stage index.
        let lastDoneStageIndex = -1;
        for (let i = update.rollout.stages.length - 1; i >= 0; i--) {
            if (update.rollout.stages[i].state === StageState.Done) {
                lastDoneStageIndex = i;
                break;
            }
        }
        if (lastDoneStageIndex === -1) return false;

        const nextStageIndex = lastDoneStageIndex + 1;
        if (nextStageIndex >= update.rollout.stages.length) return false;

        const nextStage = update.rollout.stages[nextStageIndex];
        return nextStage.state === StageState.Scheduled && nextStage.requiresConfirmation && !nextStage.isConfirmed;
    }

    const isWaitingOnConfirm = isWaitingOnStageConfirm();

    return (<div className="update_header">
        <div className="update_header_titlesSection">
            <StateBar state={update.state} />
            <h1 className="update_header_titleWrap ellipsis">
                <StateIcon state={update.state} />
                <StateColor className="update_header_title" state={update.state}>
                    {update.name}
                </StateColor>
            </h1>
            <StateColor state={update.state} className="update_header_subtitle ellipsis">
                {!isWaitingOnConfirm && (<>
                    <b>{t('updateState.' + update.state)}</b> - {stateTexts[update.state]}
                </>)}
                {isWaitingOnConfirm && (<>
                    <b>Awaiting confirmation</b> - Waiting on next stage to be confirmed.
                </>)}
            </StateColor>
        </div>
        <div className="update_header_countsSection">
            <DeviceCounts
                animateInProgress={update.state === UpdateState.Running}
                counts={update.counts.update}
                hasStarted={update.state >= UpdateState.Running}
            />
        </div>
        <div className="update_header_buttonsSection">
            <SplitButton size="small" theme="redOutline" {...actionButtonProps}>
                {(update.state === UpdateState.Draft || update.state === UpdateState.AwaitApproval) && (<>
                    <Link className="dropdown-item" to={resultsURL}>
                        View affected devices
                    </Link>
                </>)}
                {(update.state === UpdateState.AwaitApproval || update.state === UpdateState.Scheduled) && (<>
                    <Dropdown.Item
                        disabled={isInAPICall || !canModify}
                        onClick={openRevertDraftModal}
                    >
                        Revert to draft
                    </Dropdown.Item>
                </>)}
                {update.state === UpdateState.Running && (<>
                    <Dropdown.Item
                        disabled={isInAPICall || !canModify || model.isChangingSchedule}
                        onClick={model.startScheduleChange}
                    >
                        Start schedule change
                    </Dropdown.Item>
                </>)}

                {UsersService.oktaUserInfo.role === UserRole.Admin && (<>
                    <Dropdown.Item onClick={openSaveTemplateModal}>
                        Save as template
                    </Dropdown.Item>
                </>)}

                <Dropdown.Item onClick={openUpdateHelpModal}>
                    Help
                </Dropdown.Item>
                
                {/* Danger buttons section */}
                
                {update.state === UpdateState.Draft && update.id && (<>
                    <Dropdown.Divider />
                    <Dropdown.Item
                        className="dropdown-item-danger"
                        disabled={isInAPICall || !canModify} 
                        onClick={openDeleteModal}
                    >
                        Delete update
                    </Dropdown.Item>
                </>)}
                {update.state === UpdateState.Running && (<>
                    <Dropdown.Divider />
                    <Dropdown.Item
                        className="dropdown-item-danger"
                        disabled={isInAPICall || !canModify || model.isChangingSchedule}
                        onClick={openCancelUpdateModal}
                    >
                        Cancel update
                    </Dropdown.Item>
                </>)}
            </SplitButton>
        </div>
        <ApproveModal
            key={'approveModal_' + approveModalKeyRef.current}
            model={model}
            mode={approveModalMode}
            onClose={closeApproveModal}
            show={showApproveModal}
        />
        <CancelUpdateModal
            key={'cancelUpdateModal_' + cancelUpdateModalKeyRef.current} // Using a key to make the modal reset when we reopen it.
            model={model}
            onClose={closeCancelUpdateModal}
            show={showCancelModal}
        />
        <DeleteModal
            key={'deleteModal_' + deleteModalKeyRef.current} // Using a key to make the modal reset when we reopen it.
            model={model}
            onCancel={closeDeleteModal}
            onSuccess={onDeleteSuccess}
            show={showDeleteModal}
        />
        <RevertDraftModal
            key={'revertDraftModal_' + revertDraftModalKeyRef.current} // Using a key to make the modal reset when we reopen it.
            model={model}
            onClose={closeRevertDraftModal}
            show={showRevertDraftModal}
        />
        <UpdateHelpModal
            onHide={closeUpdateHelpModal}
            show={showUpdateHelpModal}
        />
        <SaveTemplateModal
            key={'saveTemplateModal_' + saveTemplateModalKeyRef.current} // Using a key to make the modal reset when we reopen it.
            model={model}
            onClose={closeSaveTemplateModal}
            show={showSaveTemplateModal}
        />
    </div>);
}